/* eslint-disable unicorn/no-useless-undefined */

import { Card, Inline, Notification } from '@omnipresentgroup/design-system';
import { useGetExpense } from '@omnipresentgroup/domains';
import {
  type Expense,
  ExpenseStatusEnum,
  isExpenseArchived,
} from '@omnipresentgroup/types';
import { useMemo } from 'react';

import { ExpenseDetailsList } from './ExpenseDetailsList';

export const ExpenseDetails = ({
  expenseId,
  expense,
  rejectionMessage,
  requestedChangeMessage,
  omnipresentTenantId,
}: {
  rejectionMessage?: string;
  requestedChangeMessage?: string;
  omnipresentTenantId?: string;
} & (
  | {
      expenseId: string;
      expense?: never;
    }
  | { expenseId?: never; expense: Expense }
)) => {
  const { data: fetchedExpense, isLoading: isExpenseLoading } = useGetExpense({
    expenseId,
    enabled: !!expenseId && !expense,
  });

  const _expense = useMemo(
    () => expense || fetchedExpense,
    [expense, fetchedExpense],
  );

  return (
    <>
      {isExpenseArchived(_expense) && (
        <Notification
          intent="info"
          id="expense-archived"
          title="Archived expense"
          description="This expense has been archived and is no longer editable."
        />
      )}
      {_expense?.requestedChange && (
        <Notification
          intent="info"
          id={ExpenseStatusEnum.INFO_NEEDED}
          title="Request change"
          description={`${
            requestedChangeMessage ??
            'A change has been requested for the expense:'
          } ${_expense.requestedChange}`}
        />
      )}
      {_expense?.rejectionReason && (
        <Notification
          id={ExpenseStatusEnum.REJECTED}
          intent="error"
          title="Expense rejected"
          description={`${
            rejectionMessage ??
            'The expense has been rejected for the following reason:'
          } ${_expense.rejectionReason}`}
        />
      )}
      <Inline justify="center">
        <Card maxW={600} p="32" w="stretch">
          <ExpenseDetailsList
            expense={_expense || ({} as Expense)}
            isLoading={isExpenseLoading}
            border={false}
            omnipresentTenantId={omnipresentTenantId}
          />
        </Card>
      </Inline>
    </>
  );
};
